import React, { useEffect, useState } from 'react';
import { isMobile } from '@/utils';
import * as queryString from 'query-string';
import useJsApi from '@/hooks/useJsApi';
import useOAuth2 from '@/hooks/useOAuth2';
import useTheme from '@/hooks/useTheme';
import { deleteUrlQueryParam } from '@/utils/urlUtils';
import getAccountType from '@/utils/getAccountType';
import { setToken } from '@/utils/fetch';
import getIsNoNeedSign from '@/utils/getIsNoNeedSign';
import { useSelector } from '@@/plugin-dva/exports';
import { useDispatch, UserState } from 'umi';
import { buildSignature } from '@/utils/signUtis';
import MergedLocaleProvider from './MergedLocaleProvider';
import { getTokenByCode, exchangeYiWangToken, getLoginUserInfo } from '../services/UserApi';

export default function (props) {
  /**
   * 需保證以下才加載頁面
   * 1. 獲取到 jwtToken
   * 2. JsApi 初始化完成
   * 否則會導致請求缺失 Request Header 或 JsApi 報錯 "Permission Denied"
   * 對接 OAuth2 及 JsApi 簽名後可設置為 process.env.NODE_ENV === 'development'
   */
  const [needAuth, setNeedAuth] = useState(true);
  // const [needAuth, setNeedAuth] = useState(process.env.NODE_ENV === 'development');
  const [initialized, setInitialized] = useState(false);
  const _query = queryString.parse(window.location.search);
  const { profile } = useSelector(({ user }: { user: UserState }) => user);
  const dispatch = useDispatch();

  useTheme(_query);
  const { init: initJsApi } = useJsApi();

  useOAuth2({
    handleCode: async (code) => {
      // 獲取到 code, 實現 getTokenByCode 請求後端授權接口獲取 jwtToken/token
      // 請求後端授權接口中需傳遞 redirectUri; 需與授權時傳遞的 redirectUri 保持一致
      // 否則會報錯: Redirect uri: xxx does not match the authorization code"
      const accountType = await getAccountType();
      getTokenByCode({
        code,
        accountType,
        redirectUri: deleteUrlQueryParam(['code']),
      })
        .then(async ({ success, data, message }) => {
          if (success) {
            // 1. 设置主token
            setToken(data);
            // 2. 获取用户信息并更新到store
            const res = await getLoginUserInfo();
            dispatch({ type: 'user/update', profile: res.data });
            // 3. 使用获取到的 euid 去获取 ADOPT_TOKEN
            const adoptToken = await fetchAdoptToken(res.data.euid);
            if (adoptToken) {
              localStorage.setItem('ADOPT_TOKEN', adoptToken);
            }
            setInitialized(true);
            setTimeout(() => {
              initJsApi();
              exchangeYiWangToken();
            }, 200);
          } else {
            console.error(message);
          }
        })
        .catch((e) => {
          console.error(e);
          setInitialized(true); // ← 异常也要设置
        });
    },
  });

  useEffect(() => {
    // 不需要登錄就可訪問
    if (getIsNoNeedSign()) {
      setNeedAuth(false);
      setInitialized(true);
    }
  }, []);

  // 不需要鑒權
  if (!needAuth) {
    return <MergedLocaleProvider>{props?.children}</MergedLocaleProvider>;
  }

  // 鑒權重
  if (needAuth && !initialized) return <div> </div>;

  // 完成鑑權後開始渲染頁面
  const Layout = isMobile() ? require('./BaseLayout').default : require('./WebLayout').default;
  return <Layout {...props} />;
}

const fetchAdoptToken = async (euid: string): Promise<string | null> => {
  try {
    const cachedToken = localStorage.getItem('ADOPT_TOKEN');
    if (cachedToken) {
      return cachedToken;
    }

    if (!euid || euid === '' || euid === null || euid === undefined) {
      return null;
    }

    const paramsForSignature = { euid };
    const requestData = buildSignature(paramsForSignature);

    const response = await fetch('/api/adoptappli/getToken', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    const res = await response.json();

    if (res.code === 200 && res.data?.token) {
      return res.data.token;
    }

    return null;
  } catch (err) {
    console.error('获取ADOPT_TOKEN失败:', err);
    return null;
  }
};
